import { useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ReactComponent as AscendingSortingIcon } from '../../../assets/icons/main/ascending_sorting.svg';
import { ReactComponent as DescendingSortingIcon } from '../../../assets/icons/main/descending_sorting.svg';
import { ReactComponent as NoSortIcon } from '../../../assets/icons/main/no-sort.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/main/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/main/plus-circle-icon.svg';
import { IconButton, InputAdornment, useTheme } from '@mui/material';
import { ReactComponent as EyeIcon } from '../../../assets/icons/main/eye-icon.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/icons/main/eye-off-icon.svg';

const Styles = {
    tableWrapper: { display: 'flex', flexDirection: 'column', gap: 1,maxHeight:'250px' },
    dataGridWrapper: { width: '100%', marginTop: '10px' },
    dataGrid: {
        '&.MuiDataGrid-root': {
            borderColor: 'transparent !important',
        },
        '.MuiDataGrid-columnHeader:first-of-type': {
            paddingLeft: '0px',
        },
        '&.MuiDataGrid-root .MuiDataGrid-withBorderColor': {
            borderColor: 'transparent !important',
        },
        '& .MuiDataGrid-cell': {
            borderColor: 'transparent !important',
        },
        '.MuiDataGrid-cell:first-of-type': {
            paddingLeft: '0px',
        },
        '.MuiDataGrid-columnHeader:last-child': {
            paddingRight: '0px',
        },
        '.MuiDataGrid-cell:last-child': {
            paddingRight: '0px',
        },
        // '.MuiDataGrid-cell:nth-last-of-type(2)': {
        //     paddingRight: '0px',
        // },
        '&.MuiDataGrid-root .MuiDataGrid-row': {
            ':hover': {
                filter: 'none',
                backgroundColor: 'transparent !important',
            }
        },
        '& .MuiDataGrid-cell:hover': {
            borderColor: 'transparent !important',
        },
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
        },
    },
    stack: {
        fontSize: '1rem',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    icon: { width: '13px', height: '13px' },
    errorAddWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    addNewBtn: { display: 'flex', alignSelf: 'flex-end', '& .MuiButton-startIcon': { mr: 0 } },
};

type RowDataType = { id: number; key: string; value: string };
interface PropsTypes {
    selectedSecretId?: number | null;
    data: RowDataType[];
    setData: React.Dispatch<React.SetStateAction<RowDataType>>;
    error?: boolean;
    isFromSecrets?: boolean;
    isFromTemplate?: boolean;
    preData?: any;
    template_id?: any;
    readOnly?: boolean;
    overlayMsg?: string;
    hasAccessTo?: any;
}

interface CustomTextFieldPropsTypes {
    name: string;
    value: string;
    error: undefined | boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    style?: any;
    disabled?: boolean;
    hiddenIcon?: boolean;
    type?: any;
    readOnly?: boolean;

}

const CustomTextField = ({ name, value, error, onChange, style, disabled, hiddenIcon, type, readOnly }: CustomTextFieldPropsTypes) => {
    const [fieldValue, setFieldValue] = useState(value);
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    useEffect(() => {
        setFieldValue(value)
    }, [value])
    return (
        <TextField
            variant='outlined'
            name={name}
            size='small'
            value={fieldValue}
            autoComplete={type === 'password' ? 'new-password' : 'off'}
            type={showPassword ? 'text' : type}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(e.target.value);
                onChange(e);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                event.stopPropagation();
            }}
            error={error}
            sx={style}
            disabled={readOnly || disabled}
            InputProps={{
                readOnly: readOnly,
                autoComplete: 'off',
                endAdornment: (
                    hiddenIcon ? <InputAdornment position="end">
                        <IconButton
                            aria-label={
                                showPassword ? 'hide the password' : 'display the password'
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            edge="end"
                        >
                            {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                        </IconButton>
                    </InputAdornment> : ''
                )
            }}
        />
    );
};

const SecretMetaTags = ({ selectedSecretId, data, setData, error, isFromSecrets, isFromTemplate, template_id, readOnly, overlayMsg, hasAccessTo }: PropsTypes) => {
    const themeData: any = useTheme();
    const [rows, setRows] = useState<{ id: number; key: string; value: string }[]>([]);
    useEffect(() => {
        if (data?.length) {
            setRows(data);
        } else {
            setRows([{ id: 1, key: '', value: '' }]);
        }
    }, [selectedSecretId, data?.length])

    const debounceOnChange = useMemo(
        () =>
            debounce((updatedRows) => {
                setData(updatedRows);
            }, 200),
        []
    );

    useEffect(() => {
        debounceOnChange(rows);
    }, [JSON.stringify(rows)]);

    const handleAddRow = () => {
        const id = rows.length + 1;
        setRows((oldRows) => [...oldRows, { id, key: '', value: '' }]);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, rowId: string | number) => {
        const { name, value } = e.target;

        const updatedRows = rows.map((row: RowDataType) => {
            if (row.id === rowId) {
                return {
                    ...row,
                    [name]: value,
                };
            }
            return row;
        });

        setRows(updatedRows);
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        setRows((prevRows) =>
            prevRows
                .filter((row) => row.id !== id)
                .map((row: RowDataType, idx: number) => {
                    return { ...row, id: idx + 1 };
                })
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'key',
            headerName: 'Key',
            flex: 1,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).key || '';
                return (
                    <CustomTextField
                        name='key'
                        value={fieldValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
                        error={error && isEmpty(fieldValue)}
                        disabled={(template_id > 0)}
                        style={{ width: '100%' }}
                        readOnly={readOnly}
                    />
                );
            },
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 1,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).value || '';
                return (
                    <CustomTextField
                        name='value'
                        value={fieldValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
                        error={error && isEmpty(fieldValue)}
                        style={{ width: '100%' }}
                        type={'password'}
                        hiddenIcon={true}
                        readOnly={readOnly}
                    />
                );
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            align: 'center',
            width: 66,
            cellClassName: 'actions',
            getActions: ({ id }: { id: GridRowId }) => {
                const disabledDeleteIcon = rows.length === 1 ? id === 1 : false || readOnly || !hasAccessTo.delete
                return readOnly ? [] : [<GridActionsCellItem icon={<DeleteIcon style={{ color: themeData.palette.custom.light, opacity: disabledDeleteIcon ? 0.5 : 1 }} />} label='Delete' onClick={handleDeleteClick(id)} disabled={disabledDeleteIcon} />];
            },
        },
    ];

    const secretColumns: GridColDef[] = [
        {
            field: 'key',
            headerName: 'Key',
            flex: 1,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).key || '';
                return (
                    <CustomTextField
                        name='key'
                        value={fieldValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
                        error={error && isEmpty(fieldValue)}
                        style={{ width: '100%' }}
                        disabled={(template_id > 0)}
                        readOnly={readOnly}
                    />
                );
            },
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 1,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).value || '';
                return (
                    <CustomTextField
                        name='value'
                        value={fieldValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
                        error={error && isEmpty(fieldValue)}
                        style={{ width: '100%' }}
                        type={'password'}
                        hiddenIcon={true}
                        readOnly={readOnly}
                    />
                );
            },
        }
    ];

    const templateCols: GridColDef[] = [
        {
            field: 'key',
            headerName: 'Key',
            flex: 1,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).key || '';
                return (
                    <CustomTextField
                        name='key'
                        value={fieldValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
                        error={error && isEmpty(fieldValue)}
                        style={{ width: '100%' }}
                        readOnly={readOnly}
                        disabled={readOnly}
                    />
                );
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 50,
            cellClassName: 'actions',
            getActions: ({ id }: { id: GridRowId }) => {
                const disabledDeleteIcon = rows.length === 1 ? id === 1 : false || readOnly || !hasAccessTo.delete;
                return [<GridActionsCellItem icon={<DeleteIcon style={{ color: themeData.palette.custom.light, opacity: disabledDeleteIcon ? 0.5 : 1 }} />} label='Delete' onClick={handleDeleteClick(id)} disabled={disabledDeleteIcon} />];
            },
        },
    ]

    return (
        <Box sx={Styles.tableWrapper}>
            <Box sx={[Styles.dataGridWrapper, { height: rows.length ? rows.length === 1 ? '125px' : `${(rows.length * 48) * 2}px` : '125px', maxHeight: '250px', overflowX: 'hidden',overflowY: 'scroll' }]}>
                <DataGrid
                    rows={rows}
                    columns={isFromTemplate ? templateCols : (template_id > 0) ? secretColumns : columns}
                    disableColumnMenu
                    hideFooterPagination
                    hideFooter
                    disableRowSelectionOnClick
                    slots={{
                        columnSortedAscendingIcon: () => <AscendingSortingIcon style={{ ...Styles.icon, color: themeData.palette.custom.light }} />,
                        columnSortedDescendingIcon: () => <DescendingSortingIcon style={{ ...Styles.icon, color: themeData.palette.custom.light }} />,
                        columnUnsortedIcon: () => <NoSortIcon style={{ ...Styles.icon, color: themeData.palette.custom.light }} />,
                        noRowsOverlay: () => <Stack sx={{ ...Styles.stack, color: themeData.palette.text.primary }}>{overlayMsg ? overlayMsg : 'No keys added!'}</Stack>,
                    }}
                    sx={Styles.dataGrid}
                    rowHeight={64}
                />
            </Box>
            <Box sx={Styles.errorAddWrapper}>
                {error ? (
                    <Typography variant='body2' color='error' fontSize='1rem' marginLeft={1.5}>
                        {overlayMsg ? overlayMsg : 'Please enter Metadata!'}
                    </Typography>
                ) : (
                    <Box></Box>
                )}
                {!readOnly && <Button variant='text' disabled={(template_id > 0)} startIcon={<PlusIcon style={{ color: (template_id > 0) ? '#9CA3B6' : themeData.palette.primary.main }} />} onClick={handleAddRow} sx={{
                    ...Styles.addNewBtn,
                    "&:disabled": {
                        backgroundColor: 'transparent',
                        display: 'flex',
                        justifyContent: 'center',
                        color: '#9CA3B6',
                        '.MuiButton-startIcon': {
                            color: '#e0e0e0',
                            marginBottom: '4px'
                        }
                    }
                }}>
                    Add New Item
                </Button>}
            </Box>
        </Box>
    );
};

export default SecretMetaTags;
